import type { ZiaFormSection, ZiaFormTemplate } from './ziaFormsTemplates';

export const ZIA_FORMS_SITE_MEDIA_SECTION_ID = 'site-media';
export const ZIA_FORMS_SITE_PHOTOS_FIELD_ID = 'siteMediaPhotos';
export const ZIA_FORMS_SITE_VIDEO_FIELD_ID = 'siteMediaVideo';
export const ZIA_FORMS_SITE_VIDEO_MAX_SECONDS = 60;
export const ZIA_FORMS_SITE_VIDEO_MAX_BYTES = 50 * 1024 * 1024;
export const ZIA_FORMS_EVIDENCE_IMAGE_LIMIT = 6;

export const ZIA_FORMS_IMAGE_MIMES = ['image/jpeg', 'image/jpg', 'image/png'] as const;
export const ZIA_FORMS_VIDEO_MIMES = ['video/mp4', 'video/quicktime', 'video/webm'] as const;

export function isZiaFormsImageMime(mime: string | null | undefined): boolean {
  return (ZIA_FORMS_IMAGE_MIMES as readonly string[]).includes(String(mime || '').toLowerCase());
}

export function isZiaFormsVideoMime(mime: string | null | undefined): boolean {
  return (ZIA_FORMS_VIDEO_MIMES as readonly string[]).includes(String(mime || '').toLowerCase());
}

/** Unreadable metadata is allowed — the API byte cap is the backstop. */
export function ziaFormsSiteVideoIsTooLong(seconds: number): boolean {
  if (!Number.isFinite(seconds) || seconds <= 0) return false;
  return seconds > ZIA_FORMS_SITE_VIDEO_MAX_SECONDS + 0.5;
}

export function readZiaFormsVideoDurationSeconds(file: File): Promise<number> {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') {
      resolve(0);
      return;
    }
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.onloadedmetadata = () => {
      const duration = el.duration;
      URL.revokeObjectURL(el.src);
      resolve(Number.isFinite(duration) ? duration : 0);
    };
    el.onerror = () => {
      URL.revokeObjectURL(el.src);
      resolve(0);
    };
    el.src = URL.createObjectURL(file);
  });
}

export function siteMediaAttachmentExt(mimeType: string): string {
  const mime = String(mimeType || '').toLowerCase();
  if (mime === 'image/png') return 'png';
  if (mime === 'video/quicktime') return 'mov';
  if (mime === 'video/webm') return 'webm';
  if (mime === 'video/mp4') return 'mp4';
  return 'jpg';
}

export function createZiaFormsSiteMediaSection(): ZiaFormSection {
  return {
    id: ZIA_FORMS_SITE_MEDIA_SECTION_ID,
    title: 'Site photos / video',
    description: 'Optional. Pictures and/or a 1-minute video of the site.',
    fields: [
      {
        id: ZIA_FORMS_SITE_PHOTOS_FIELD_ID,
        label: 'Site photos',
        type: 'attachment',
        helperText: 'Optional pictures of the site.',
      },
      {
        id: ZIA_FORMS_SITE_VIDEO_FIELD_ID,
        label: 'Site video (1 minute max)',
        type: 'attachment',
        helperText: 'Optional. One clip, up to 1 minute.',
      },
    ],
  };
}

export function withSiteMediaSection(template: ZiaFormTemplate): ZiaFormTemplate {
  if (template.sections.some((section) => section.id === ZIA_FORMS_SITE_MEDIA_SECTION_ID)) {
    return template;
  }
  return {
    ...template,
    sections: [...template.sections, createZiaFormsSiteMediaSection()],
  };
}

/** Count selected image names so a text-only PDF cannot silently omit their bytes. */
export function ziaFormsExpectedPhotoCount(template: ZiaFormTemplate, values: Record<string, string | string[]>): number {
  return template.sections.flatMap(section => section.fields)
    .filter(field => field.type === 'attachment' && field.id !== ZIA_FORMS_SITE_VIDEO_FIELD_ID)
    .reduce((count, field) => {
      const value = values[field.id];
      const names = Array.isArray(value) ? value : value ? [value] : [];
      return count + names.filter(name => field.id === ZIA_FORMS_SITE_PHOTOS_FIELD_ID || /\.(png|jpe?g|webp|heic|heif)$/i.test(name)).length;
    }, 0);
}
